import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart3, Plus, Trash2, Play, Pause, Square, Archive, RotateCcw,
  Loader2, ChevronDown, ChevronUp, X, AlertTriangle, GripVertical, Pencil, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: 'text-white/30 bg-white/5',
  active: 'text-green-400 bg-green-400/10',
  paused: 'text-yellow-400 bg-yellow-400/10',
  closed: 'text-red-400 bg-red-400/10',
  archived: 'text-white/20 bg-white/5',
};

const STATUS_ACTIONS = {
  draft: [{ action: 'activate', label: 'Activate', icon: Play, color: 'text-green-400 bg-green-400/10' }],
  active: [
    { action: 'pause', label: 'Pause', icon: Pause, color: 'text-yellow-400 bg-yellow-400/10' },
    { action: 'close', label: 'Close', icon: Square, color: 'text-red-400 bg-red-400/10' },
  ],
  paused: [
    { action: 'resume', label: 'Resume', icon: Play, color: 'text-green-400 bg-green-400/10' },
    { action: 'close', label: 'Close', icon: Square, color: 'text-red-400 bg-red-400/10' },
  ],
  closed: [{ action: 'archive', label: 'Archive', icon: Archive, color: 'text-white/30 bg-white/5' }],
  archived: [],
};

const PollsAdmin = ({ token }) => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPoll, setExpandedPoll] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [bulkCandidates, setBulkCandidates] = useState('');
  const [newCandidate, setNewCandidate] = useState({});
  const [confirmReset, setConfirmReset] = useState(null);
  const refreshRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchPolls = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/polls`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setPolls(await r.json());
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchPolls(); }, [fetchPolls]);

  // Auto-refresh while any poll is active
  useEffect(() => {
    const hasActive = polls.some(p => p.status === 'active');
    if (hasActive) {
      refreshRef.current = setInterval(fetchPolls, 5000);
    }
    return () => { if (refreshRef.current) clearInterval(refreshRef.current); };
  }, [polls, fetchPolls]);

  const fetchPollDetail = useCallback(async (pollId) => {
    const r = await fetch(`${API}/api/admin/polls/${pollId}`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) return r.json();
    return null;
  }, [token]);

  const createPoll = async () => {
    if (!newTitle.trim()) return;
    const r = await fetch(`${API}/api/admin/polls`, {
      method: 'POST', headers, body: JSON.stringify({ title: newTitle, description: newDesc }),
    });
    if (r.ok) {
      const poll = await r.json();
      // Add bulk candidates if provided
      const names = bulkCandidates.split('\n').map(s => s.trim()).filter(Boolean);
      for (const name of names) {
        await fetch(`${API}/api/admin/polls/${poll.id}/candidates`, {
          method: 'POST', headers, body: JSON.stringify({ name }),
        });
      }
      toast.success(`Poll created${names.length > 0 ? ` with ${names.length} candidates` : ''}`);
      setNewTitle(''); setNewDesc(''); setBulkCandidates(''); setCreating(false);
      fetchPolls();
      setExpandedPoll(poll.id);
    } else { toast.error('Failed to create poll'); }
  };

  const changeStatus = async (pollId, action) => {
    // Prevent activating with 0 candidates
    if (action === 'activate') {
      const detail = await fetchPollDetail(pollId);
      if (!detail || !detail.candidates || detail.candidates.length === 0) {
        toast.error('Add at least one candidate before activating');
        return;
      }
    }
    const r = await fetch(`${API}/api/admin/polls/${pollId}/status/${action}`, {
      method: 'POST', headers,
    });
    if (r.ok) { toast.success(`Poll ${action}d`); fetchPolls(); }
    else { toast.error(`Failed to ${action} poll`); }
  };

  const resetPoll = async (pollId) => {
    const r = await fetch(`${API}/api/admin/polls/${pollId}/reset`, { method: 'POST', headers });
    if (r.ok) { toast.success('Votes reset'); setConfirmReset(null); fetchPolls(); }
    else { toast.error('Reset failed'); }
  };

  const deletePoll = async (pollId) => {
    const r = await fetch(`${API}/api/admin/polls/${pollId}`, { method: 'DELETE', headers });
    if (r.ok) { toast.success('Poll deleted'); if (expandedPoll === pollId) setExpandedPoll(null); fetchPolls(); }
    else { toast.error('Delete failed'); }
  };

  const addCandidate = async (pollId) => {
    const name = (newCandidate[pollId] || '').trim();
    if (!name) return;
    const r = await fetch(`${API}/api/admin/polls/${pollId}/candidates`, {
      method: 'POST', headers, body: JSON.stringify({ name }),
    });
    if (r.ok) { setNewCandidate(prev => ({ ...prev, [pollId]: '' })); fetchPolls(); }
    else { toast.error('Failed to add candidate'); }
  };

  const removeCandidate = async (pollId, candidateId) => {
    await fetch(`${API}/api/admin/polls/${pollId}/candidates/${candidateId}`, {
      method: 'DELETE', headers,
    });
    fetchPolls();
  };

  // Count active categories
  const activePolls = polls.filter(p => p.status === 'active' || p.status === 'paused');

  return (
    <div data-testid="polls-admin">
      <h2 className="text-white font-bold text-lg mb-1 flex items-center gap-2">
        <BarChart3 size={18} className="text-[#E91E8D]" /> Live Polls
      </h2>
      <p className="text-white/30 text-xs mb-2">Create ranked audience voting categories. Each poll becomes a tab on the public page.</p>

      {activePolls.length > 0 && (
        <div className="flex items-center gap-2 mb-4 px-2 py-1.5 rounded-lg bg-green-400/5 border border-green-400/10">
          <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          <span className="text-green-400 text-[10px] font-bold">
            {activePolls.length} active {activePolls.length === 1 ? 'category' : 'categories'} — showing as tabs on public poll
          </span>
        </div>
      )}

      {loading ? (
        <div className="py-6 text-center"><Loader2 size={20} className="text-[#E91E8D] animate-spin mx-auto" /></div>
      ) : (
        <>
          {/* Create Button */}
          {!creating ? (
            <button onClick={() => setCreating(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E91E8D]/10 border border-[#E91E8D]/20 hover:bg-[#E91E8D]/20 text-[#E91E8D] text-xs font-semibold transition-colors mb-5"
              data-testid="create-poll-btn">
              <Plus size={12} /> New Poll Category
            </button>
          ) : (
            <div className="glass-card p-4 mb-5 space-y-3" data-testid="create-poll-form">
              <div className="flex items-center justify-between">
                <span className="text-white text-xs font-bold">New Poll Category</span>
                <button onClick={() => { setCreating(false); setNewTitle(''); setNewDesc(''); setBulkCandidates(''); }} className="text-white/30 hover:text-white"><X size={14} /></button>
              </div>
              <div>
                <label className="text-white/40 text-[10px] font-bold tracking-wider uppercase block mb-1">Category Title</label>
                <input value={newTitle} onChange={e => setNewTitle(e.target.value)}
                  placeholder="e.g. Most Respected Men on Clubhouse"
                  className="input-med w-full !text-sm" data-testid="new-poll-title" />
              </div>
              <div>
                <label className="text-white/40 text-[10px] font-bold tracking-wider uppercase block mb-1">Description (optional)</label>
                <input value={newDesc} onChange={e => setNewDesc(e.target.value)}
                  placeholder="Vote for your choice"
                  className="input-med w-full !text-sm" data-testid="new-poll-desc" />
              </div>
              <div>
                <label className="text-white/40 text-[10px] font-bold tracking-wider uppercase block mb-1">
                  Candidates — one per line
                </label>
                <textarea value={bulkCandidates} onChange={e => setBulkCandidates(e.target.value)}
                  placeholder={"Marcus\nDarius\nJames\nAndre\nDavid"}
                  rows={6}
                  className="input-med w-full !text-sm resize-none font-mono leading-relaxed"
                  data-testid="new-poll-candidates" />
                <p className="text-white/20 text-[9px] mt-1">
                  {bulkCandidates.split('\n').filter(s => s.trim()).length} candidates entered
                </p>
              </div>
              <button onClick={createPoll} disabled={!newTitle.trim()}
                className="btn-pink !text-xs disabled:opacity-40" data-testid="create-poll-submit">
                Create Category
              </button>
            </div>
          )}

          {/* Poll List */}
          <div className="space-y-3">
            {polls.map(poll => (
              <PollCard
                key={poll.id}
                poll={poll}
                expanded={expandedPoll === poll.id}
                onToggle={() => setExpandedPoll(expandedPoll === poll.id ? null : poll.id)}
                onStatus={changeStatus}
                onDelete={deletePoll}
                onReset={resetPoll}
                onAddCandidate={addCandidate}
                onRemoveCandidate={removeCandidate}
                newCandidate={newCandidate[poll.id] || ''}
                onNewCandidateChange={(v) => setNewCandidate(prev => ({ ...prev, [poll.id]: v }))}
                confirmReset={confirmReset}
                setConfirmReset={setConfirmReset}
                fetchDetail={fetchPollDetail}
                token={token}
              />
            ))}
          </div>

          {polls.length === 0 && !creating && (
            <p className="text-white/20 text-xs text-center py-8">No polls yet. Create your first category to get started.</p>
          )}
        </>
      )}
    </div>
  );
};

const PollCard = ({
  poll, expanded, onToggle, onStatus, onDelete, onReset,
  onAddCandidate, onRemoveCandidate, newCandidate, onNewCandidateChange,
  confirmReset, setConfirmReset, fetchDetail, token,
}) => {
  const [detail, setDetail] = useState(null);
  const [editingName, setEditingName] = useState(null);
  const [editNameVal, setEditNameVal] = useState('');
  const statusClass = STATUS_COLORS[poll.status] || 'text-white/30 bg-white/5';
  const actions = STATUS_ACTIONS[poll.status] || [];

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (expanded) {
      fetchDetail(poll.id).then(d => { if (d) setDetail(d); });
    }
  }, [expanded, poll.id, fetchDetail]);

  useEffect(() => {
    if (expanded && poll.total_votes !== undefined) {
      fetchDetail(poll.id).then(d => { if (d) setDetail(d); });
    }
  }, [expanded, poll.total_votes, poll.id, fetchDetail]);

  const candidates = detail?.candidates || [];
  const results = detail?.results || [];
  const totalVotes = detail?.total_votes || 0;
  const hasCandidates = candidates.length > 0;

  const updateCandidateName = async (candidateId, newName) => {
    await fetch(`${API}/api/admin/polls/${poll.id}/candidates/${candidateId}`, {
      method: 'PUT', headers, body: JSON.stringify({ name: newName }),
    });
    setEditingName(null);
    fetchDetail(poll.id).then(d => { if (d) setDetail(d); });
  };

  return (
    <div className="glass-card overflow-hidden" data-testid={`poll-card-${poll.id}`}>
      {/* Header */}
      <button onClick={onToggle} className="w-full px-4 py-3 flex items-center gap-3 text-left">
        <div className="flex-1 min-w-0">
          <p className="text-white text-xs font-bold truncate">{poll.title}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${statusClass}`}>{poll.status}</span>
            <span className="text-white/20 text-[10px]">{poll.total_votes || 0} votes</span>
            {poll.leader && (
              <span className="text-[#E91E8D] text-[10px] font-semibold">
                #1 {poll.leader.name} — {poll.leader.percentage}%
              </span>
            )}
          </div>
        </div>
        {expanded ? <ChevronUp size={14} className="text-white/20" /> : <ChevronDown size={14} className="text-white/20" />}
      </button>

      {/* Expanded Detail */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-white/5 pt-3">
          {/* No candidates warning */}
          {!hasCandidates && poll.status === 'draft' && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-yellow-400/10 border border-yellow-400/20">
              <AlertTriangle size={12} className="text-yellow-400 flex-shrink-0" />
              <span className="text-yellow-400 text-[10px] font-bold">Add candidates below before activating this poll</span>
            </div>
          )}

          {/* Status Controls */}
          <div className="flex flex-wrap gap-2">
            {actions.map(a => (
              <button key={a.action} onClick={() => onStatus(poll.id, a.action)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold ${a.color} transition-colors`}>
                <a.icon size={10} /> {a.label}
              </button>
            ))}
            {poll.status !== 'archived' && (
              <button onClick={() => setConfirmReset(confirmReset === poll.id ? null : poll.id)}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold text-orange-400 bg-orange-400/10">
                <RotateCcw size={10} /> Reset
              </button>
            )}
            <button onClick={() => onDelete(poll.id)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold text-red-400 bg-red-400/10 ml-auto">
              <Trash2 size={10} /> Delete
            </button>
          </div>

          {/* Reset Confirmation */}
          {confirmReset === poll.id && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-orange-400/10 border border-orange-400/20">
              <span className="text-orange-400 text-[10px] font-bold">Reset all votes?</span>
              <button onClick={() => onReset(poll.id)}
                className="px-2 py-0.5 rounded bg-orange-400 text-black text-[10px] font-bold">Confirm</button>
              <button onClick={() => setConfirmReset(null)}
                className="text-white/30 text-[10px]">Cancel</button>
            </div>
          )}

          {/* Live Results */}
          {results.length > 0 && totalVotes > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/30 text-[10px] font-bold tracking-wider uppercase">Rankings — {totalVotes} votes</span>
              </div>
              <div className="space-y-1">
                {results.map((r, idx) => (
                  <div key={r.candidate_id} className="flex items-center gap-2 px-2 py-1.5 rounded bg-white/[0.02]">
                    <span className={`text-[10px] font-bold w-4 text-center ${idx === 0 ? 'text-[#E91E8D]' : 'text-white/25'}`}>
                      {r.rank}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white/70 text-[11px] font-medium truncate">{r.name}</span>
                      </div>
                      <div className="w-full bg-white/5 rounded-full h-1 mt-0.5">
                        <div className={`h-1 rounded-full transition-all duration-500 ${idx === 0 ? 'bg-[#E91E8D]' : 'bg-white/15'}`}
                          style={{ width: `${Math.max(r.percentage, 1)}%` }} />
                      </div>
                    </div>
                    <span className="text-white/30 text-[10px] font-mono w-10 text-right">{r.votes}</span>
                    <span className={`text-[10px] font-bold w-12 text-right ${idx === 0 ? 'text-[#E91E8D]' : 'text-white/30'}`}>
                      {r.percentage}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Candidate Management */}
          <div>
            <span className="text-white/30 text-[10px] font-bold tracking-wider uppercase block mb-2">
              Candidates ({candidates.length})
            </span>
            <div className="space-y-1 mb-2">
              {candidates.map((c, idx) => (
                <div key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded bg-white/[0.02] border border-white/5 group">
                  <GripVertical size={10} className="text-white/10" />
                  <span className="text-white/20 text-[10px] font-bold w-4 text-center">{idx + 1}</span>
                  {editingName === c.id ? (
                    <div className="flex items-center gap-1 flex-1">
                      <input value={editNameVal} onChange={e => setEditNameVal(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') updateCandidateName(c.id, editNameVal); if (e.key === 'Escape') setEditingName(null); }}
                        className="input-med flex-1 !text-xs !py-0.5" autoFocus />
                      <button onClick={() => updateCandidateName(c.id, editNameVal)} className="text-green-400"><Check size={10} /></button>
                      <button onClick={() => setEditingName(null)} className="text-white/30"><X size={10} /></button>
                    </div>
                  ) : (
                    <>
                      <span className="text-white/60 text-[11px] flex-1 truncate">{c.name}</span>
                      <button onClick={() => { setEditingName(c.id); setEditNameVal(c.name); }}
                        className="text-white/10 group-hover:text-white/30"><Pencil size={9} /></button>
                    </>
                  )}
                  <button onClick={() => onRemoveCandidate(poll.id, c.id)}
                    className="text-red-400/30 hover:text-red-400"><X size={10} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={newCandidate} onChange={e => onNewCandidateChange(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') onAddCandidate(poll.id); }}
                placeholder="Add candidate name..."
                className="input-med flex-1 !text-xs" data-testid={`add-candidate-input-${poll.id}`} />
              <button onClick={() => onAddCandidate(poll.id)} disabled={!newCandidate.trim()}
                className="px-3 py-1.5 rounded-lg bg-[#E91E8D]/10 text-[#E91E8D] text-[10px] font-bold disabled:opacity-30">
                <Plus size={10} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PollsAdmin;
